import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Play, Pause, Eye, Lightning, ArrowRight } from "@phosphor-icons/react";

const BLANK = {
  title: "", description: "", cta_label: "Claim offer", cta_href: "/consultation",
  start_at: "", end_at: "", status: "active", priority: 1, display_rule: "homepage",
};

export default function Promotions() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/promotions");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, priority: Number(editing.priority) || 0 };
      if (editing.id) await api.put(`/admin/promotions/${editing.id}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/promotions/${id}`);
    toast.success("Deleted");
    await load();
  };

  const toggleStatus = async (p) => {
    const next = p.status === "active" ? "paused" : "active";
    try {
      await api.put(`/admin/promotions/${p.id}`, { ...p, status: next });
      toast.success(next === "active" ? "Promotion activated" : "Promotion paused");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline mb-2">Promotions</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Active & scheduled promotions</h1>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-signal" data-testid="promos-create">
          <Plus size={16} weight="bold" /> New promotion
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.length === 0 && <p className="text-sm text-zinc-500 font-mono">No promotions.</p>}
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 p-6" data-testid={`promo-card-${p.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="status-pill" style={{ color: p.status === "active" ? "#10B981" : "#71717A" }}>{p.status}</span>
                {(p.display_rule || "homepage").startsWith("code:") ? (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 bg-zinc-950 text-white" title={`Only visible when ?promo=${extractCode(p.display_rule)} is in the URL`}>
                    🔒 Event-only · {extractCode(p.display_rule)}
                  </span>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 bg-zinc-100 text-zinc-700" title="Visible to everyone on the homepage">
                    🌐 Public
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewing(p)}
                  className="text-zinc-600 hover:text-zinc-900"
                  data-testid={`promo-preview-${p.id}`}
                  title="Preview on homepage"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => toggleStatus(p)}
                  className={`text-xs font-mono uppercase tracking-widest px-2 py-1 border ${p.status === "active" ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
                  data-testid={`promo-toggle-${p.id}`}
                  title={p.status === "active" ? "Pause this promotion" : "Activate this promotion"}
                >
                  {p.status === "active" ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
                  {p.status === "active" ? "Pause" : "Activate"}
                </button>
                <button onClick={() => setEditing(p)} className="text-zinc-600 hover:text-zinc-900" data-testid={`promo-edit-${p.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => remove(p.id)} className="text-zinc-600 hover:text-red-600" data-testid={`promo-delete-${p.id}`}><Trash size={16} /></button>
              </div>
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h3>
            <p className="text-sm text-zinc-600 mb-4">{p.description}</p>
            <div className="grid grid-cols-3 gap-2 text-xs font-mono text-zinc-500 border-t border-zinc-100 pt-3">
              <div>Views<br /><span className="text-zinc-950 text-sm">{p.views || 0}</span></div>
              <div>Conv.<br /><span className="text-zinc-950 text-sm">{p.conversions || 0}</span></div>
              <div>Priority<br /><span className="text-zinc-950 text-sm">{p.priority || 0}</span></div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="promo-editor">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-semibold">{editing.id ? "Edit promotion" : "New promotion"}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-500 text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="promo-title-input" />
              <label className="block text-sm">
                <span className="font-semibold text-zinc-800 mb-1 block">Description</span>
                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <Input label="CTA label" value={editing.cta_label} onChange={(v) => setEditing({ ...editing, cta_label: v })} />
                <Input label="CTA link" value={editing.cta_href} onChange={(v) => setEditing({ ...editing, cta_href: v })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <DateTimeInput label="Starts at" value={editing.start_at} onChange={(v) => setEditing({ ...editing, start_at: v })} testid="promo-start-at" />
                <DateTimeInput label="Ends at" value={editing.end_at} onChange={(v) => setEditing({ ...editing, end_at: v })} testid="promo-end-at" />
              </div>
              <p className="text-xs text-zinc-500 -mt-2">Leave both blank for &ldquo;always live&rdquo; (while status is Active). Times are in your local timezone.</p>
              <div className="grid grid-cols-2 gap-4">
                <label className="text-sm">Status
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="block w-full mt-1 border border-zinc-200 px-3 py-2 bg-white" data-testid="promo-status-select">
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="archived">Archived</option>
                  </select>
                </label>
                <Input label="Priority" value={editing.priority} onChange={(v) => setEditing({ ...editing, priority: v })} />
              </div>

              {/* Visibility / display_rule */}
              <div className="border-t border-zinc-200 pt-4">
                <div className="font-semibold text-sm text-zinc-800 mb-1">Who can see this?</div>
                <p className="text-xs text-zinc-500 mb-3">Event-exclusive promos are hidden from the public homepage. They only appear when a visitor arrives with the matching promo code in the URL (e.g. <code className="font-mono">?promo=hl1may</code>) — typically via your event QR codes, NFC cards, or shared links.</p>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer p-3 border border-zinc-200 hover:border-zinc-400">
                    <input
                      type="radio"
                      name="visibility"
                      checked={!(editing.display_rule || "").startsWith("code:")}
                      onChange={() => setEditing({ ...editing, display_rule: "homepage" })}
                      data-testid="promo-visibility-public"
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-semibold">Public — show on the homepage to everyone</div>
                      <div className="text-xs text-zinc-500">Visible to all visitors. Replaces the IdeaLab banner in the prime slot while this is active.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer p-3 border border-zinc-200 hover:border-zinc-400">
                    <input
                      type="radio"
                      name="visibility"
                      checked={(editing.display_rule || "").startsWith("code:")}
                      onChange={() => setEditing({ ...editing, display_rule: "code:" + (extractCode(editing.display_rule) || "") })}
                      data-testid="promo-visibility-gated"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">Event-exclusive — only via promo code</div>
                      <div className="text-xs text-zinc-500 mb-2">Hidden from public visitors. Shows only when someone lands with <code className="font-mono">?promo=&lt;code&gt;</code>.</div>
                      {(editing.display_rule || "").startsWith("code:") && (
                        <input
                          type="text"
                          value={extractCode(editing.display_rule)}
                          onChange={(e) => setEditing({ ...editing, display_rule: "code:" + e.target.value.trim().toLowerCase() })}
                          placeholder="e.g. hl1may"
                          className="w-full border border-zinc-200 px-3 py-2 bg-white text-sm"
                          data-testid="promo-code-input"
                        />
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-ink">Cancel</button>
              <button onClick={save} className="btn-signal" data-testid="promo-save">Save</button>
            </div>
          </div>
        </div>
      )}
      {previewing && (
        <PromoPreviewModal item={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function PromoPreviewModal({ item, onClose }) {
  const willShowOnHomepage = (() => {
    const rule = item.display_rule || "homepage";
    if (item.status !== "active") return false;
    if (rule === "homepage" || rule === "global") return true;
    if (rule.startsWith("code:")) return false; // only shows when ?promo=<code> in URL or storage
    return false;
  })();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="promo-preview-modal">
      <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <p className="overline">Homepage preview</p>
            <h2 className="font-semibold tracking-tight">How &ldquo;{item.title}&rdquo; will appear</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 text-2xl leading-none" data-testid="promo-preview-close">×</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Visibility status */}
          <div className={`p-4 border ${willShowOnHomepage ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <div className="text-sm">
              {willShowOnHomepage ? (
                <>✓ Live on the homepage right now. While this is active, it takes the prime banner slot — the IdeaLab promo is hidden until you Pause this.</>
              ) : item.status !== "active" ? (
                <>This promotion is <strong>{item.status}</strong>. Activate it to see it on the homepage. The IdeaLab banner is currently filling this slot.</>
              ) : (
                <>This promotion is gated by display_rule <code className="font-mono">{item.display_rule}</code>. It only appears when a visitor arrives with a matching <code>?promo=</code> URL or stored code. The IdeaLab banner shows in this slot to general visitors.</>
              )}
            </div>
          </div>

          {/* Faithful banner reproduction */}
          <div className="border border-zinc-200">
            <div className="bg-gradient-to-r from-[hsl(var(--appistry-signal))] to-[hsl(var(--appistry-teal))] text-white">
              <div className="px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-white/15 border border-white/25 flex items-center justify-center">
                    <Lightning size={20} weight="fill" color="white" />
                  </div>
                  <div>
                    <div className="overline text-white/80">Active promotion</div>
                    <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {item.title || "(no title)"}
                    </div>
                    <div className="text-sm text-white/85 mt-0.5">{item.description}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 bg-white text-[hsl(var(--appistry-signal))] px-5 py-3 font-semibold">
                  {item.cta_label || "Claim offer"}
                  <ArrowRight size={16} weight="bold" />
                </span>
              </div>
            </div>
          </div>

          {/* Quick fields recap */}
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <Meta label="Status" value={item.status} />
            <Meta label="Display rule" value={item.display_rule || "homepage"} />
            <Meta label="Priority" value={item.priority ?? 0} />
            <Meta label="CTA target" value={item.cta_href || "—"} />
            <Meta label="Starts" value={item.start_at || "—"} />
            <Meta label="Ends" value={item.end_at || "—"} />
            <Meta label="Views" value={item.views || 0} />
            <Meta label="Conversions" value={item.conversions || 0} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 p-2.5">
      <div className="font-mono uppercase tracking-widest text-[9px] text-zinc-500 mb-1">{label}</div>
      <div className="text-zinc-800 truncate">{String(value)}</div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid={testid} />
    </label>
  );
}

/**
 * Datetime picker that converts cleanly between the API's ISO-8601 UTC strings
 * and the browser's `datetime-local` value (which is naive local time).
 */
function DateTimeInput({ label, value, onChange, testid }) {
  // ISO → local-naive for the <input> (e.g. "2026-06-18T19:30")
  const toLocalNaive = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // local-naive → ISO (UTC) for persistence
  const toIsoUtc = (localNaive) => {
    if (!localNaive) return "";
    const d = new Date(localNaive);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
  };

  return (
    <label className="block text-sm">
      <span className="font-semibold text-zinc-800 mb-1 flex items-center justify-between">
        <span>{label}</span>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs font-normal text-zinc-400 hover:text-zinc-700" data-testid={testid ? `${testid}-clear` : undefined}>
            clear
          </button>
        )}
      </span>
      <input
        type="datetime-local"
        value={toLocalNaive(value)}
        onChange={(e) => onChange(toIsoUtc(e.target.value))}
        className="w-full border border-zinc-200 px-3 py-2 bg-white"
        data-testid={testid}
      />
    </label>
  );
}

function extractCode(rule) {
  if (!rule || !rule.startsWith("code:")) return "";
  return rule.slice(5);
}
